import React, { useState, useEffect, useCallback } from 'react'
import {
  Form,
  Button,
  Input,
  Row,
  Col,
  Switch,
  Typography,
  Tooltip,
  FormInstance,
  Upload
} from 'antd'
import GoogleMap from 'google-map-react'
import { Store } from '@slerp/controls'
import { kml } from '@tmcw/togeojson'
import { DOMParser } from 'xmldom'
import { useSession } from '@slerp/accounts'

import MapStyles from '../MapStyles.json'

export const MAPS_API_KEY = process.env.REACT_APP_MAPS_API_KEY || ''

export const getZoom = (radius: number) => {
  if (radius >= 30) return 8
  if (radius >= 20) return 9
  if (radius >= 10) return 10
  if (radius >= 6) return 10.5
  if (radius >= 5) return 11
  if (radius >= 3) return 11.5
  if (radius >= 2) return 12
  if (radius >= 1) return 12.5
  if (radius >= 0.5) return 13
  return 14
}

export const drawMapCenter = (mapRef: any, coords: number[]) => {
  return new mapRef.maps.Marker({
    position: { lat: coords[0], lng: coords[1] },
    map: mapRef.map,
    title: 'Shop'
  })
}

type MapRef = {
  map: any
  maps: any
}

type Coordinate = [number, number, number]

interface DeliveryAreaProps {
  path: []
  center: any
  radius: number
  onUpdate: React.Dispatch<any>
  mapRef: MapRef
  options: any
}

interface LatLng {
  lat: string
  lng: string
}
class DeliveryArea {
  path: LatLng[]
  polygon: any
  center: any
  radius: number
  onUpdate: React.Dispatch<any>

  constructor(props: DeliveryAreaProps) {
    this.radius = props.radius
    this.center = props.center

    this.path = props.path ? props.path : this.calculateInitialPath()
    this.onUpdate = props.onUpdate

    this.polygon = new props.mapRef.maps.Polygon({
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillOpacity: 0.05,
      editable: true,
      map: props.mapRef.map,
      ...props.options
    })

    this.setPath(this.path)
  }

  public resetToDefault() {
    this.setPath(this.calculateInitialPath())
  }

  calculateInitialPath() {
    const POLYGON_POINTS = 8

    const SKEW_FACTOR = 1.6
    const MILES_TO_DEGREES = 1.0 / 69
    const latRadius = this.radius * MILES_TO_DEGREES
    const lngRadius = latRadius * SKEW_FACTOR

    const latCenter = this.center.lat
    const lngCenter = this.center.lng

    let paths = []

    for (let i = 0; i < POLYGON_POINTS; i++) {
      const theta = (Math.PI * 2 * i) / POLYGON_POINTS
      const lat = latCenter + Math.cos(theta) * latRadius
      const lng = lngCenter + Math.sin(theta) * lngRadius

      paths.push({ lat, lng })
    }

    return paths
  }

  private updatePath() {
    this.path = this.polygon
      .getPath()
      .getArray()
      .map((latLng: any) => ({ lat: latLng.lat(), lng: latLng.lng() }))
    this.onUpdate(this.path)
  }

  private addListeners() {
    const path = this.polygon.getPath()
    path.addListener('set_at', this.updatePath.bind(this))
    path.addListener('insert_at', this.updatePath.bind(this))
  }

  getPath() {
    return this.path
  }

  setPath(path: any) {
    this.path = path
    this.polygon.setPaths(path)
    this.addListeners()
    this.onUpdate(this.path)
  }

  setActive(active: boolean) {
    this.polygon.setVisible(active)
  }
}

const DeliveryAreaSettings = (props: {
  form: FormInstance
  store: Store
  useStorePreOrderSettings: boolean
  disabled?: boolean
}) => {
  const { user } = useSession()

  const { store, form, useStorePreOrderSettings, disabled = false } = props

  const DEFAULT_CUSTOM_AREA_RADIUS_IN_MILES = 4
  const DEFAULT_BUSY_AREA_SCALE = 0.7

  const settings =
    useStorePreOrderSettings && store.pre_order_settings?.length
      ? store.pre_order_settings[0].delivery_area_setting
      : store.settings

  const [deliveryPaths, setDeliveryPaths] = useState(settings?.delivery_area)
  const [busyDeliveryPaths, setBusyDeliveryPaths] = useState(
    settings?.busy_delivery_area
  )

  const [deliveryArea, setDeliveryArea] = useState<DeliveryArea | undefined>(
    undefined
  )
  const [busyDeliveryArea, setBusyDeliveryArea] = useState<
    DeliveryArea | undefined
  >(undefined)

  let mapRef = { map: null, maps: null }

  const resetToDefault = () => {
    deliveryArea?.resetToDefault()
    busyDeliveryArea?.resetToDefault()
  }

  const createMapOptions = (maps: any) => ({ styles: MapStyles })

  const storeLocation = {
    lat: store.address.geom.coordinates[0],
    lng: store.address.geom.coordinates[1]
  }

  const centerOnStoreLocation = useCallback(() => {
    mapRef.map.setCenter(storeLocation)
  }, [mapRef, storeLocation])

  useEffect(() => {
    form.setFieldsValue({
      delivery_area: deliveryPaths,
      busy_delivery_area: busyDeliveryPaths
    })
  }, [deliveryPaths, busyDeliveryPaths])

  // TODO: draw shop log as with slerp-shop splash map
  const drawCenter = useCallback(() => {
    mapRef && drawMapCenter(mapRef, store.address.geom.coordinates)
  }, [mapRef, store.address.geom.coordinates])

  const handleGoogleApiLoaded = ({ map, maps }: any) => {
    mapRef = { map, maps }

    setDeliveryArea(
      new DeliveryArea({
        center: {
          lat: store.address.geom.coordinates[0],
          lng: store.address.geom.coordinates[1]
        },
        radius: DEFAULT_CUSTOM_AREA_RADIUS_IN_MILES,
        path: settings?.delivery_area,
        onUpdate: setDeliveryPaths,
        mapRef: mapRef,
        options: {
          fillColor: '#6580a3',
          strokeColor: '#6580a3'
        }
      })
    )

    setBusyDeliveryArea(
      new DeliveryArea({
        center: {
          lat: store.address.geom.coordinates[0],
          lng: store.address.geom.coordinates[1]
        },
        radius: DEFAULT_CUSTOM_AREA_RADIUS_IN_MILES * DEFAULT_BUSY_AREA_SCALE,
        path: settings?.busy_delivery_area,
        onUpdate: setBusyDeliveryPaths,
        mapRef: mapRef,
        options: {
          fillColor: '#a38065',
          strokeColor: '#a38065',
          visible: settings?.busy_delivery_area_enabled === true
        }
      })
    )

    drawCenter()
    centerOnStoreLocation()
  }

  const toggleBusyDeliveryArea = (checked: boolean) => {
    busyDeliveryArea?.setActive(checked)
  }

  const readKML = (file: File) => {
    const reader = new FileReader()

    reader.readAsText(file)
    reader.onload = () => {
      const parser = new DOMParser()
      const kmlFromFile = parser.parseFromString(reader.result)
      const geoJSON = kml(kmlFromFile)
      const deliveryPaths = geoJSON.features[0].geometry.coordinates[0].map(
        (coords: Coordinate) => {
          return { lng: coords[0], lat: coords[1] }
        }
      )

      setDeliveryPaths(deliveryPaths)
      deliveryArea?.setPath(deliveryPaths)
    }
    return false
  }

  return (
    <>
      <Form.Item name='delivery_area' hidden>
        <Input />
      </Form.Item>
      <Form.Item name='busy_delivery_area' hidden>
        <Input />
      </Form.Item>
      <Row>
        <Tooltip
          title={`Enabling Busy Delivery Area will set this store's activity to Busy.`}
        >
          <Row style={{ margin: 0 }}>
            <Col>
              <Form.Item
                name='busy_delivery_area_enabled'
                valuePropName='checked'
                initialValue={!!settings?.busy_delivery_area_enabled}
              >
                <Switch
                  onChange={toggleBusyDeliveryArea}
                  size='small'
                  disabled={disabled}
                />
              </Form.Item>
            </Col>
            <Col style={{ marginTop: 6 }}>
              <label htmlFor='busy_delivery_area_enabled'>
                <Typography.Text style={{ marginLeft: 12 }}>
                  Activate Busy Delivery Area
                </Typography.Text>
              </label>
            </Col>
          </Row>
        </Tooltip>
        <Col span={24}>
          <div style={{ width: '100%', height: '70vh' }}>
            <GoogleMap
              bootstrapURLKeys={{
                key: MAPS_API_KEY
              }}
              defaultCenter={{ lat: 51.5137137, lng: -0.1285204 }}
              defaultZoom={getZoom(DEFAULT_CUSTOM_AREA_RADIUS_IN_MILES)}
              options={createMapOptions}
              yesIWantToUseGoogleMapApiInternals={true}
              onGoogleApiLoaded={handleGoogleApiLoaded}
            ></GoogleMap>
          </div>
        </Col>
      </Row>

      <Row justify='center'>
        {user?.role === 'superadmin' && (
          <Upload beforeUpload={readKML}>
            <Button>Upload KML</Button>
          </Upload>
        )}
        <Button onClick={resetToDefault} disabled={disabled}>
          Reset to default
        </Button>
      </Row>
    </>
  )
}

export default DeliveryAreaSettings
